import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { companyInfo } from '../data/mock';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  // Liens prioritaires (visibles dès md) et secondaires (visibles uniquement en lg+)
  const priorityLinks = [
    { path: '/', label: 'Accueil' },
    { path: '/services', label: 'Services' },
    { path: '/villas', label: 'Villas' },
    { path: '/contact', label: 'Contact' }
  ];

  const secondaryLinks = [
    { path: '/about', label: 'À propos' },
    { path: '/terrains', label: 'Terrains' },
    { path: '/appartements', label: 'Appartements' },
    { path: '/projects', label: 'Nos Projets' }
  ];

  const allLinks = [
    { path: '/', label: 'Accueil' },
    { path: '/about', label: 'À propos' },
    { path: '/services', label: 'Services' },
    { path: '/villas', label: 'Villas' },
    { path: '/terrains', label: 'Terrains' },
    { path: '/appartements', label: 'Appartements' },
    { path: '/projects', label: 'Nos Projets' },
    { path: '/contact', label: 'Contact' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      {/* Top bar */}
      <div className="bg-slate-900 text-white py-2">
        <div className="container mx-auto px-4 flex justify-between items-center text-sm">
          <div className="flex items-center gap-4">
            <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-2 hover:text-amber-400 transition-colors">
              <Phone className="w-4 h-4" />
              <span className="hidden sm:inline">{companyInfo.phone}</span>
            </a>
            <a href={`tel:${companyInfo.phone2}`} className="flex items-center gap-2 hover:text-amber-400 transition-colors">
              <Phone className="w-4 h-4" />
              <span className="hidden sm:inline">{companyInfo.phone2}</span>
            </a>
            <a href={`mailto:${companyInfo.email}`} className="flex items-center gap-2 hover:text-amber-400 transition-colors">
              <Mail className="w-4 h-4" />
              <span className="hidden md:inline">{companyInfo.email}</span>
            </a>
          </div>
          <div className="text-xs hidden md:block">
            {companyInfo.slogan}
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 flex-shrink-0 mr-8">
            <img
              src="/logo-pam-immo.png"
              alt="PAM IMMO SERVICES"
              className="h-12 w-auto object-contain"
            />
            <div className="flex flex-col">
              <span className="text-2xl font-bold text-slate-900 whitespace-nowrap">{companyInfo.name}</span>
              <span className="text-xs text-amber-600 font-medium whitespace-nowrap">{companyInfo.slogan}</span>
            </div>
          </Link>

          {/* Desktop Navigation - All links visible on lg+ */}
          <nav className="hidden lg:flex items-center gap-6 flex-nowrap">
            {allLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium whitespace-nowrap transition-colors hover:text-amber-600 ${
                  isActive(link.path) ? 'text-amber-600' : 'text-slate-700'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Button asChild className="bg-amber-600 hover:bg-amber-700 text-white flex-shrink-0">
              <Link to="/contact">Nous contacter</Link>
            </Button>
          </nav>

          {/* Medium screens: priority links + hamburger for the rest */}
          <div className="hidden md:flex lg:hidden items-center gap-4 flex-nowrap">
            <nav className="flex items-center gap-4 flex-nowrap">
              {priorityLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium whitespace-nowrap transition-colors hover:text-amber-600 ${
                    isActive(link.path) ? 'text-amber-600' : 'text-slate-700'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-lg hover:bg-slate-100 transition-colors flex-shrink-0"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Small screens: hamburger only */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Dropdown menu (mobile: all links, md screens: secondary links only) */}
        {isMenuOpen && (
          <nav className="lg:hidden pb-4 space-y-2">
            {/* On mobile: show all links */}
            <div className="md:hidden">
              {allLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`block py-2 px-4 rounded-lg transition-colors ${
                    isActive(link.path)
                      ? 'bg-amber-600 text-white'
                      : 'hover:bg-slate-100 text-slate-700'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
            {/* On md screens: show only secondary links (priority already visible) */}
            <div className="hidden md:block lg:hidden">
              {secondaryLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`block py-2 px-4 rounded-lg transition-colors ${
                    isActive(link.path)
                      ? 'bg-amber-600 text-white'
                      : 'hover:bg-slate-100 text-slate-700'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;